import { FC } from 'react';
import { BoxV2 as Box, Flex } from 'portal-commons';

interface TabsProps {
  activeTab: string;
  tabs: { id: string; label: string }[];
  onChange: (tab: string) => void;
  'data-testid'?: string;
}

const commonBorderStyle = '1px solid';
const borderColor = '#B3B7B8';

const Tabs: FC<TabsProps> = ({
  tabs,
  activeTab,
  onChange,
  children,
  'data-testid': dataTestId,
}) => {
  const tabButtons = tabs.map((tab, idx) => {
    const isActive = tab.id === activeTab;

    return (
      <div key={tab.id}>
        <Box
          as="button"
          type="button"
          onClick={(e) => {
            onChange(tab.id);
          }}
          role="tab"
          aria-selected={isActive}
          aria-controls={`panel-${tab.id}`}
          data-testid={`tab-${tab.id}`}
          sx={{
            fontSize: 'H500',
            textAlign: 'left',
            fontWeight: 'semiBold',
            cursor: 'pointer',
            textDecoration: 'none',
            color: !isActive ? '#fff' : 't.black80',
            backgroundColor: !isActive ? 't.blue120' : '#fff',
            padding: '9px 14px',
            minWidth: '180px',
            display: 'inline-block',
            borderRadius: '8px 8px 0 0',
            lineHeight: 'normal',
            border: commonBorderStyle,
            borderColor,
            borderBottom: 'none', // Hides bottom border if active
            borderLeft: idx > 0 ? 'none' : undefined,
            '&:focus': {
              outline: 'none',
              textDecoration: 'none',
            },
          }}
        >
          {tab.label}
        </Box>
      </div>
    );
  });

  return (
    <Flex
      sx={{ flexDirection: 'column', width: '100%' }}
      data-testid={dataTestId}
    >
      <Flex
        role="tablist"
        sx={{
          borderColor,
          position: 'relative',
          top: '1px',
        }}
      >
        {tabButtons}
      </Flex>
      <Box
        sx={{
          border: commonBorderStyle,
          borderColor,
          padding: '16px 24px 24px 24px',
        }}
      >
        {children}
      </Box>
    </Flex>
  );
};

const TabPanel: FC<{ value: string; hidden: boolean }> = ({
  value,
  children,
  hidden,
}) => {
  return (
    <Box
      role="tabpanel"
      hidden={hidden}
      id={`tabpanel-${value}`}
      aria-labelledby={`tabpanel-${value}`}
      sx={{
        color: 't.black70',
      }}
    >
      {children}
    </Box>
  );
};

export { Tabs, TabPanel };
